import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Particles } from "@/components/catalog/Particles";
import { useAuth } from "@/context/AuthContext";
import { useGuest } from "@/context/GuestContext";
import { formatApiError } from "@/lib/api";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const Field = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">{label}</span>
    <input data-testid={testId} {...props} className="w-full rounded-xl border border-[#D4AF37]/25 bg-black/50 px-4 py-3 text-white placeholder:text-[#5A5A62] outline-none transition-colors focus:border-[#FFD86A]" />
  </label>
);

export default function Login({ mode }) {
  const isRegister = mode === "register";
  const { login, register } = useAuth();
  const { guestOn } = useGuest();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (isRegister) await register(form.name, form.email, form.password);
      else await login(form.email, form.password);
      navigate("/profiles", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profiles";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#050505] text-white grain" data-testid={`${mode}-page`}>
      <img src="/assets/art/hero_redline_reign.jpg" alt="" className="absolute inset-0 h-full w-full object-cover object-[70%_center] opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      <Particles count={22} />
      <div className="relative z-10 mx-auto grid min-h-screen max-w-[1500px] items-center gap-12 px-5 sm:px-8 lg:px-12 py-10 lg:grid-cols-[1.15fr_1fr]">
        <div className="space-y-8">
          <Logo size="lg" to="/login" />
          <div className="hidden sm:block space-y-5 max-w-xl">
            <p className="eyebrow">AETRYX — Watch. Create. Publish.</p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] gold-text">Watch movies. Create movies.</h1>
            <p className="text-base text-white/70 leading-relaxed">Build your own cinematic universe. Ten original worlds — from the neon nights of Las Vegas to the golden dunes of the Desert Kings — waiting for you.</p>
          </div>
        </div>
        <div className="w-full max-w-md justify-self-center lg:justify-self-end">
          <form onSubmit={submit} className="glass bg-[#0B0B0D]/80 rounded-3xl p-7 sm:p-9 shadow-[0_30px_80px_rgba(0,0,0,0.75)] space-y-5" data-testid="auth-form">
            <div>
              <p className="eyebrow mb-2">{isRegister ? "Join the studio" : "Welcome back"}</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">{isRegister ? "Create your account" : "Sign in"}</h2>
            </div>
            <button type="button" data-testid="google-login-btn" onClick={google} className="flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-white text-black py-3 font-bold hover:bg-white/90 transition-colors">
              <GoogleIcon /> Continue with Google
            </button>
            <div className="flex items-center gap-3 text-xs text-[#8B8B8B]"><span className="h-px flex-1 bg-[#D4AF37]/20" /> or with email <span className="h-px flex-1 bg-[#D4AF37]/20" /></div>
            {isRegister && <Field label="Name" testId="auth-name-input" value={form.name} onChange={set("name")} placeholder="Your name" autoComplete="name" />}
            <Field label="Email" testId="auth-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" autoComplete="email" />
            <Field label="Password" testId="auth-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="••••••••" autoComplete={isRegister ? "new-password" : "current-password"} />
            {error && <p data-testid="auth-error" className="rounded-lg border border-[#E50914]/40 bg-[#E50914]/10 px-3 py-2 text-sm text-[#ff8a8a]">{error}</p>}
            <button type="submit" disabled={busy} data-testid="auth-submit-btn" className="btn-gold w-full py-3.5 text-base disabled:opacity-70">
              {busy && <Loader2 className="h-5 w-5 animate-spin" />} {isRegister ? "Create Account" : "Sign In"}
            </button>
            {!isRegister && <Link to="/forgot-password" data-testid="auth-forgot-link" className="block text-center text-sm text-[#D4AF37] hover:text-[#FFD86A] hover:underline">Forgot password?</Link>}
            <p className="text-center text-sm text-[#8B8B8B]">
              {isRegister ? "Already a member? " : "New to AETRYX? "}
              <Link to={isRegister ? "/login" : "/register"} data-testid="auth-switch-link" className="font-semibold text-[#FFD86A] hover:underline">
                {isRegister ? "Sign in" : "Create an account"}
              </Link>
            </p>
            {guestOn && <Link to="/" data-testid="auth-browse-guest-link" className="block text-center text-xs text-[#8B8B8B] hover:text-[#FFD86A]">Browse as a guest</Link>}
          </form>
        </div>
      </div>
    </div>
  );
}
